import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Frown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import flowerData from "./Data.json";

// ==========================================
// UTILS
// ==========================================
const standardSlugify = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .replace(/([^0-9a-z-\s])/g, "")
    .replace(/(\s+|-+)/g, "-")
    .trim();
};

const formatPrice = (price) => {
  if (!price) return "Liên hệ Floraison";
  const cleanPrice = price.toString().replace(/\D/g, "");
  if (!cleanPrice) return "Liên hệ Floraison";
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
  }).format(cleanPrice);
};

// ==========================================
// TOAST NOTIFICATION COMPONENT
// ==========================================
const ToastNotification = ({ toast }) => {
  return (
    <AnimatePresence>
      {toast.show && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          style={styles.toastWrapper}
        >
          <div style={styles.toastContainer}>{toast.message}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ==========================================
// GLOBAL STYLES & RESPONSIVE CSS
// ==========================================
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Montserrat:wght@200;300;400;500;600&display=swap');
  
  .luxury-serif { font-family: 'Cormorant Garamond', serif !important; }
  .luxury-sans { font-family: 'Montserrat', sans-serif !important; }
  .smooth-transition { transition: all 0.3s ease; }
  .img-overflow-hidden { overflow: hidden; position: relative; }
  
  .custom-scrollbar::-webkit-scrollbar { width: 4px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 4px; }
  
  .apple-blur {
    backdrop-filter: blur(25px);
    -webkit-backdrop-filter: blur(25px);
    background-color: rgba(255, 211, 237, 0.9);
  }
  
  .studio-card-wrapper:hover .hover-img-scale {
    transform: scale(1.05);
  }

  /* ========== RESPONSIVE DESIGN ========== */
  @media (max-width: 480px) {
    .search-title-text { font-size: 26px !important; }
    .search-input-field { font-size: 14px !important; padding: 12px 35px 12px 40px !important; }
    .studio-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 2px !important; }
    .hover-product-name { font-size: 16px !important; margin-bottom: 5px !important; }
    .hover-price { font-size: 11px !important; }
    .hover-cta-btn { font-size: 9px !important; padding: 8px 16px !important; }
    .empty-text { font-size: 20px !important; text-align: center; }
    .empty-subtext { font-size: 12px !important; text-align: center; }
    .toast-container { font-size: 12px !important; padding: 12px 20px !important; max-width: 90vw !important; }
    
    /* Modal Mobile Styles */
    .modal-content { flex-direction: column !important; max-width: 95vw !important; max-height: 90vh !important; }
    .modal-image-column { flex: 0 0 auto !important; height: 280px !important; padding: 15px !important; }
    .modal-info-column { padding: 20px !important; }
    .modal-title { font-size: 22px !important; }
  }

  @media (max-width: 768px) and (min-width: 480px) {
    .search-title-text { font-size: 32px !important; }
    .studio-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 3px !important; }
    .hover-product-name { font-size: 20px !important; }
    
    /* Modal Tablet Styles */
    .modal-content { flex-direction: column !important; max-width: 90vw !important; max-height: 90vh !important; }
    .modal-image-column { height: 320px !important; padding: 25px !important; }
    .modal-title { font-size: 28px !important; }
  }

  @media (min-width: 768px) {
    /* ĐÃ FIX: Thêm class .studio-grid vào đây */
    .studio-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
      gap: 3px 3px !important;
    }
  }
`;

// ==========================================
// MAIN COMPONENT: TIMKIEM
// ==========================================
export default function Timkiem() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  
  // Trạng thái lưu trữ hoa được chọn để hiển thị Modal
  const [selectedFlower, setSelectedFlower] = useState(null);
  const inputRef = useRef(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3000);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = "auto";
      setSearchQuery("");
      setSearchResults([]);
      setSelectedFlower(null);
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // Logic tìm kiếm Realtime
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const keyword = searchQuery.toLowerCase().trim();
    const results = flowerData.filter((hoa) => {
      if (!hoa) return false;
      const tenHoa = hoa["Tên hoa"]?.toString().trim() || "";
      const yNghia = hoa["Ý nghĩa của hoa"]?.toString().trim() || "";
      const maHoa = hoa["Mã hoa"]?.toString().trim() || "";
      const tongMau = hoa["Tông màu hoa"]?.toString().trim() || "";
      const thanhPhan = hoa["Thành phần hoa"]?.toString().trim() || "";

      const isValidMaHoa = maHoa && /^flower\d+\.(png|avif|webp|jpg)$/i.test(maHoa);
      const isValidData = tenHoa && yNghia && isValidMaHoa && tenHoa !== "Chưa cập nhật tên" && !yNghia.includes("Đang cập nhật");
      if (!isValidData) return false;

      return (
        tenHoa.toLowerCase().includes(keyword) ||
        thanhPhan.toLowerCase().includes(keyword) ||
        maHoa.toLowerCase().includes(keyword) ||
        tongMau.toLowerCase().includes(keyword)
      );
    });
    setSearchResults(results);
  }, [searchQuery]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  const addToCart = (e, hoa) => {
    e.stopPropagation();
    try {
      const cart = JSON.parse(localStorage.getItem("sellixy_cart")) || [];
      const flowerId = hoa["Mã hoa"] || hoa.id || `flower-${standardSlugify(hoa["Tên hoa"] || hoa.name)}`;
      const flowerName = hoa["Tên hoa"] || hoa.name;
      const rawPrice = hoa["Giá bán"] || hoa.price || "0";
      const cleanPrice = rawPrice.toString().replace(/\D/g, "");
      const numericPrice = parseInt(cleanPrice) || 0;

      const existingItem = cart.find((item) => item.id === flowerId);
      if (existingItem) {
        existingItem.quantity = (existingItem.quantity || 1) + 1;
      } else {
        cart.push({
          id: flowerId,
          name: flowerName,
          price: numericPrice,
          img: `/flowers/${hoa["Mã hoa"]}`,
          ma: flowerId,
          quantity: 1,
        });
      }
      localStorage.setItem("sellixy_cart", JSON.stringify(cart));
      window.dispatchEvent(new Event("cartUpdated"));
      showToast(`✓ Đã thêm "${flowerName}" vào giỏ hàng!`, "success");
    } catch (error) {
      console.error("Lỗi khi thêm vào giỏ hàng:", error);
      showToast("✕ Có lỗi xảy ra, vui lòng thử lại!", "error");
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        style={styles.navSearchBtn}
        className="luxury-sans smooth-transition"
        aria-label="Mở tìm kiếm"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={styles.searchOverlay}
            className="apple-blur custom-scrollbar"
          >
            <style>{globalStyles}</style>
            
            <div style={styles.searchHeader}>
              <button onClick={() => setIsOpen(false)} style={styles.closeBtn} className="smooth-transition">
                <X size={24} strokeWidth={1.5} color="#111111" />
              </button>
            </div>

            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              style={styles.inputContainer}
            >
              <h2 style={styles.searchTitle} className="luxury-serif search-title-text">
                Khám phá tác phẩm
              </h2>
              <div style={styles.inputWrapper}>
                <Search size={20} color="#888888" strokeWidth={1.5} style={styles.inputIcon} />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Nhập tên hoa, tông màu hoặc thành phần..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={styles.searchInput}
                  className="luxury-sans search-input-field"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} style={styles.clearBtn}>
                    <X size={16} color="#888888" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            </motion.div>

            <div style={styles.resultsContainer}>
              {searchQuery.trim() !== "" && (
                <>
                  <div style={styles.resultSummary} className="luxury-sans">
                    {searchResults.length > 0
                      ? `Tìm thấy ${searchResults.length} tuyệt tác cho "${searchQuery}"`
                      : `Không tìm thấy tác phẩm nào khớp với "${searchQuery}"`}
                  </div>

                  {searchResults.length > 0 ? (
                    <div style={styles.studioGrid} className="studio-grid">
                      {searchResults.map((hoa) => (
                        <div
                          key={hoa["Mã hoa"] || Math.random()}
                          style={styles.studioCard}
                          className="studio-card-wrapper"
                          onClick={() => setSelectedFlower(hoa)}
                          onMouseEnter={() => setHoveredId(hoa["Mã hoa"])}
                          onMouseLeave={() => setHoveredId(null)}
                        >
                          <div style={styles.cardImgContainer} className="img-overflow-hidden">
                            <img
                              src={`/flowers/${hoa["Mã hoa"]}`}
                              alt={hoa["Tên hoa"]}
                              style={styles.cardImg}
                              className="smooth-transition hover-img-scale"
                            />
                            
                            <AnimatePresence>
                              {hoveredId === hoa["Mã hoa"] && (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                  style={styles.cardHoverOverlay}
                                >
                                  <motion.div
                                    initial={{ y: 10 }}
                                    animate={{ y: 0 }}
                                    exit={{ y: 10 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    <h4 style={styles.hoverProductName} className="luxury-serif hover-product-name">
                                      {hoa["Tên hoa"]}
                                    </h4>
                                    <div style={styles.hoverPriceContainer}>
                                      <span style={styles.hoverPrice} className="luxury-sans hover-price">
                                        {formatPrice(hoa["Giá bán"])}
                                      </span>
                                    </div>
                                    <button
                                      style={styles.hoverCtaBtn}
                                      className="luxury-sans smooth-transition hover-cta-btn"
                                      onClick={(e) => addToCart(e, hoa)}
                                    >
                                      Thêm vào giỏ ✧
                                    </button>
                                  </motion.div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={styles.emptyState}
                    >
                      <Frown size={42} color="#cccccc" strokeWidth={1} style={{ marginBottom: "15px" }} />
                      <p className="luxury-serif empty-text" style={styles.emptyText}>
                        Nghệ nhân chưa chế tác tác phẩm mang tên này.
                      </p>
                      <p className="luxury-sans empty-subtext" style={styles.emptySubText}>
                        Hãy thử lại với một loài hoa, tông màu hoặc thông điệp khác.
                      </p>
                    </motion.div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* POPUP MODAL THÔNG TIN CHI TIẾT */}
      <AnimatePresence>
        {selectedFlower && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modalOverlay}
            className="apple-blur"
            onClick={() => setSelectedFlower(null)}
          >
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              style={styles.modalContent}
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedFlower(null)}
                style={styles.modalCloseBtn}
                className="smooth-transition"
              >
                <X size={20} strokeWidth={1.5} color="#111111" />
              </button>

              <div style={styles.modalBody} className="modal-body-layout">
                {/* Cột Trái: Ảnh */}
                <div style={styles.modalImageWrapper} className="modal-image-column">
                  <img
                    src={`/flowers/${selectedFlower["Mã hoa"]}`}
                    alt={selectedFlower["Tên hoa"]}
                    style={styles.modalImage}
                  />
                </div>
                
                {/* Cột Phải: Thông tin chi tiết */}
                <div style={styles.modalInfoWrapper} className="modal-info-column custom-scrollbar">
                  <h3 style={styles.modalTitle} className="luxury-serif modal-title">
                    {selectedFlower["Tên hoa"]}
                  </h3>
                  <p style={styles.modalPrice} className="luxury-sans">
                    {formatPrice(selectedFlower["Giá bán"])}
                  </p>
                  
                  <div style={styles.modalDesc} className="luxury-serif">
                    {selectedFlower["Ý nghĩa của hoa"]
                      ? selectedFlower["Ý nghĩa của hoa"].split('\n').map((line, i) => (
                          <p key={i} style={{ marginBottom: '10px' }}>{line}</p>
                        ))
                      : "Một tác phẩm nghệ thuật chứa đựng thông điệp tinh tế từ Floraison."}
                  </div>
                  
                  <button
                    style={styles.modalAddToCartBtn}
                    className="luxury-sans smooth-transition"
                    onClick={(e) => {
                      addToCart(e, selectedFlower);
                      setSelectedFlower(null);
                    }}
                  >
                    Thêm vào giỏ ✧
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastNotification toast={toast} />
    </>
  );
}

// ==========================================
// STYLES
// ==========================================
const styles = {
  navSearchBtn: {
    cursor: "pointer", width: "40px", height: "40px", borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#000000", background: "transparent", border: "none", outline: "none",
  },
  searchOverlay: {
    position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
    zIndex: 9998, overflowY: "auto", display: "flex", flexDirection: "column",
  },
  searchHeader: {
    display: "flex", justifyContent: "flex-end", padding: "20px 5%",
  },
  closeBtn: {
    background: "transparent", border: "none", cursor: "pointer", padding: "10px",
    display: "flex", alignItems: "center", justifyContent: "center",
    borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.04)",
  },
  inputContainer: {
    width: "100%", maxWidth: "800px", margin: "0 auto", padding: "10px 5% 30px", textAlign: "center",
  },
  searchTitle: {
    color: "#111111", marginBottom: "25px", fontWeight: "400",
  },
  inputWrapper: {
    position: "relative", width: "100%", display: "flex", alignItems: "center",
    borderBottom: "1px solid #111111", paddingBottom: "12px",
  },
  inputIcon: {
    position: "absolute", left: "5px", top: "50%", transform: "translateY(-50%)",
  },
  searchInput: {
    width: "100%", background: "transparent", border: "none", outline: "none",
    color: "#111111", fontWeight: "300", paddingLeft: "40px", paddingRight: "35px", fontSize: "16px",
  },
  clearBtn: {
    position: "absolute", right: "5px", background: "transparent", border: "none",
    cursor: "pointer", display: "flex", alignItems: "center",
  },
  // ĐÃ FIX: Thêm Flexbox căn giữa để trị lỗi lệch phải
  resultsContainer: {
    flex: 1, padding: "20px 5% 80px", maxWidth: "1400px", margin: "0 auto", width: "100%",
    zIndex: 10, opacity: 1, visibility: "visible",
    display: "flex", flexDirection: "column", alignItems: "center" 
  },
  resultSummary: {
    fontSize: "12px", color: "#555555", letterSpacing: "1px", textTransform: "uppercase",
    marginBottom: "40px", textAlign: "center",
  },
  // ĐÃ FIX: Chuyển auto-fill thành repeat 4, thêm giới hạn maxWidth
  studioGrid: {
    display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "20px 15px", 
    width: "100%", maxWidth: "1200px" 
  },
  studioCard: {
    position: "relative", aspectRatio: "3/4", backgroundColor: "#000000", cursor: "pointer", overflow: "hidden",
  },
  cardImgContainer: {
    width: "100%", height: "100%", backgroundColor: "#000000", position: "relative",
  },
  cardImg: {
    objectFit: "contain", width: "100%", height: "100%", display: "block"
  },
  cardHoverOverlay: {
    position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.75)", display: "flex", flexDirection: "column",
    justifyContent: "center", alignItems: "center", textAlign: "center", padding: "15px", zIndex: 3,
  },
  hoverProductName: {
    color: "#ffffff", marginBottom: "8px", fontSize: "22px"
  },
  hoverPriceContainer: {
    marginBottom: "20px",
  },
  hoverPrice: {
    color: "#cccccc", letterSpacing: "1px",
  },
  hoverCtaBtn: {
    background: "transparent", border: "1px solid #ffffff", color: "#ffffff",
    textTransform: "uppercase", letterSpacing: "2px", cursor: "pointer", padding: "10px 20px",
  },
  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", paddingTop: "40px",
  },
  emptyText: {
    color: "#111111", marginBottom: "10px",
  },
  emptySubText: {
    color: "#888888",
  },
  toastWrapper: {
    position: "fixed", bottom: "40px", left: "0", right: "0", display: "flex", justifyContent: "center",
    zIndex: 10000, pointerEvents: "none",
  },
  toastContainer: {
    backgroundColor: "#111111", color: "#ffffff", padding: "14px 28px", borderRadius: "30px",
    fontSize: "13px", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.5px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
  },

  // ========== MODAL STYLES ==========
  modalOverlay: {
    position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
    zIndex: 9999, display: "flex", justifyContent: "center", alignItems: "center", padding: "20px"
  },
  modalContent: {
    backgroundColor: "#ffffff", width: "100%", maxWidth: "900px", maxHeight: "85vh",
    position: "relative", overflow: "hidden", display: "flex",
    boxShadow: "0 25px 60px rgba(0,0,0,0.15)",
  },
  modalCloseBtn: {
    position: "absolute", top: "15px", right: "15px",
    background: "rgba(0,0,0,0.05)", border: "none", borderRadius: "50%",
    width: "36px", height: "36px", display: "flex", justifyContent: "center", alignItems: "center",
    cursor: "pointer", zIndex: 10
  },
  modalBody: {
    display: "flex", width: "100%", height: "100%", overflow: "hidden"
  },
  modalImageWrapper: {
    flex: "0 0 45%", backgroundColor: "#000000", position: "relative",
    display: "flex", justifyContent: "center", alignItems: "center", padding: "30px"
  },
  modalImage: {
    maxWidth: "100%", maxHeight: "100%", objectFit: "contain"
  },
  modalInfoWrapper: {
    flex: "1 1 55%", padding: "40px", overflowY: "auto",
    display: "flex", flexDirection: "column", justifyContent: "center"
  },
  modalTitle: {
    fontSize: "32px", color: "#111111", marginBottom: "10px", fontWeight: 400
  },
  modalPrice: {
    fontSize: "18px", color: "#555555", marginBottom: "25px", letterSpacing: "1px"
  },
  modalDesc: {
    fontSize: "16px", color: "#444444", lineHeight: 1.6, marginBottom: "35px"
  },
  modalAddToCartBtn: {
    backgroundColor: "#111111", color: "#ffffff", border: "none",
    padding: "16px 32px", fontSize: "11px", textTransform: "uppercase", letterSpacing: "2px",
    cursor: "pointer", alignSelf: "flex-start", marginTop: "auto"
  }
};